import React, { useEffect } from 'react';

import { Label } from '../ui/Label';
import { Input } from '../ui/Input';
import { DAMAGE_TYPES, CLAIM_TYPES } from '../../data/roomChecklists';

// ================================================================= //
//          DamageTypeSelector — Damage & Claim Type Selection        //
// ================================================================= //
// Provides upfront damage type and claim type selection. Damage type //
// is required (shapes rules and code suggestions). Claim type and   //
// jurisdiction (ZIP code) are optional.                              //
// ================================================================= //

interface DamageTypeSelectorProps {
  /** Currently selected damage type value. */
  damageType: string;
  /** Called when damage type changes. */
  onDamageTypeChange: (value: string) => void;
  /** Currently selected claim type value. */
  claimType: string;
  /** Called when claim type changes. */
  onClaimTypeChange: (value: string) => void;
  /** Optional jurisdiction ZIP code. */
  jurisdiction?: string;
  /** Called when jurisdiction ZIP changes. */
  onJurisdictionChange?: (value: string) => void;
  /** Whether Cat 3 (contaminated water) toggle is checked. */
  cat3Toggle: boolean;
  /** Called when Cat 3 toggle changes. */
  onCat3ToggleChange: (value: boolean) => void;
}

export const DamageTypeSelector: React.FC<DamageTypeSelectorProps> = ({
  damageType,
  onDamageTypeChange,
  claimType,
  onClaimTypeChange,
  jurisdiction,
  onJurisdictionChange,
  cat3Toggle,
  onCat3ToggleChange,
}) => {
  // Reason: Auto-set Cat 3 toggle based on damage type selection.
  // Flood and wind default to checked (Cat 3 codes); water defaults to unchecked.
  // Other types leave the toggle unchanged so the user's manual choice persists.
  useEffect(() => {
    if (damageType === 'flood' || damageType === 'wind') {
      onCat3ToggleChange(true);
    } else if (damageType === 'water') {
      onCat3ToggleChange(false);
    }
  }, [damageType, onCat3ToggleChange]);
  const selectClasses =
    'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

  return (
    <div className="space-y-4">
      {/* Damage type and claim type side by side */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="scope-damage-type">
            Damage Type <span className="text-destructive">*</span>
          </Label>
          <select
            id="scope-damage-type"
            value={damageType}
            onChange={(e) => onDamageTypeChange(e.target.value)}
            className={selectClasses}
            required
          >
            <option value="">Select damage type...</option>
            {DAMAGE_TYPES.map((dt) => (
              <option key={dt.value} value={dt.value}>
                {dt.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground">
            Shapes rules applied and code suggestions.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="scope-claim-type">Claim Type</Label>
          <select
            id="scope-claim-type"
            value={claimType}
            onChange={(e) => onClaimTypeChange(e.target.value)}
            className={selectClasses}
          >
            <option value="">Select claim type...</option>
            {CLAIM_TYPES.map((ct) => (
              <option key={ct.value} value={ct.value}>
                {ct.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground">
            Filters coverage (homeowner, flood/NFIP, commercial).
          </p>
        </div>
      </div>

      {/* Claim Details — Cat 3 toggle */}
      <div className="space-y-2 rounded-md border border-input bg-muted/30 p-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Claim Details
        </p>
        <label className="flex items-start gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={cat3Toggle}
            onChange={(e) => onCat3ToggleChange(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
          />
          <div>
            <span className="text-sm font-medium text-foreground">
              Use Cat 3 line items (contaminated water)
            </span>
            <p className="text-xs text-muted-foreground">
              Checked by default for flood and wind claims. Uses WTR codes for water-damaged material removal.
            </p>
          </div>
        </label>
      </div>

      {/* ZIP code for jurisdiction */}
      {onJurisdictionChange && (
        <div className="space-y-2">
          <Label htmlFor="scope-jurisdiction">ZIP Code (Optional)</Label>
          <Input
            id="scope-jurisdiction"
            value={jurisdiction || ''}
            onChange={(e) => {
              // Reason: Only allow digits, max 5 characters for ZIP
              const val = e.target.value.replace(/\D/g, '').slice(0, 5);
              onJurisdictionChange(val);
            }}
            placeholder="e.g., 33101"
            maxLength={5}
            className="max-w-[200px]"
          />
          <p className="text-xs text-muted-foreground">
            For jurisdiction-specific building code requirements.
          </p>
        </div>
      )}
    </div>
  );
};

export default DamageTypeSelector;
